/**
 * LayoutSwitcher — F12.H T12.H.11.
 *
 * Dropdown w toolbarze buildera pokazujący nazwę bieżącego edytowanego
 * layoutu + listę wszystkich layoutów modułu + przycisk „Nowy layout".
 *
 * Wyświetla badge kategorii (jeśli layout przypisany do konkretnej)
 * lub „Uniwersalny" gdy category_id IS NULL.
 *
 * Klik na layout z listy → onSelect(layoutId) → builder przełącza
 * editowany layout (wywoła refetch + reinicjalizacja sections z layoutConfig).
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Copy,
  Download,
  FolderOpen,
  LayoutPanelTop,
  Lock,
  PanelRight,
  Pencil,
  Plus,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Tooltip } from '@/components/Tooltip';

interface Category {
  id: string;
  name: string;
  color: string;
}

/** T12.M.A.5 — minimal shape do renderowania nazw ról przy badge'u. */
interface RoleTemplate {
  id: string;
  name: string;
}

export interface LayoutOption {
  id: string;
  name: string;
  categoryId: string | null;
  isDefault: boolean;
  layoutType: string;
  /** Preferencja tenanta „pokazuj dokowany podgląd załączników po prawej" — modelowana 1:1 na `isDefault`. Opcjonalne: moduły własne (`ModuleBuilderPage`) nie mają tego pojęcia. */
  previewDockEnabled?: boolean;
  /** T12.M.A.5 — UUID-y ról z dostępem do layoutu. Puste = uniwersalny (wszyscy). */
  roleIds?: string[];
  /**
   * B.2+B.3 — marker layoutu centralnego (zmaterializowany z biblioteki widoków
   * kartoteki przez ITE). NOT NULL ⟹ producent — Edytuj/Usuń są zablokowane
   * (nietykalne dla tenanta; zmiana treści tylko przez re-materializację).
   * Opcjonalne i domyślnie `undefined` (= brak blokady) — moduły własne
   * (`custom_module_layouts`, ModuleBuilderPage) nie mają tego pojęcia i go
   * nigdy nie ustawiają.
   */
  sourceViewSlug?: string | null;
  /**
   * Układ dostarczony z MENU modułu kreatora ITE (NOT NULL ⟹ tylko do odczytu, nigdy domyślny):
   * Edytuj/Usuń/„Ustaw jako domyślny" zablokowane — zmiany przez „Duplikuj".
   */
  menuSourceModuleId?: string | null;
}

interface LayoutSwitcherProps {
  layouts: LayoutOption[];
  currentLayoutId: string | null;
  categories: Category[];
  /** T12.M.A.5 — opcjonalna lista ról tenanta (do renderowania nazw przy badge). */
  roleTemplates?: RoleTemplate[];
  onSelect: (layoutId: string) => void;
  /** Utwórz nowy layout. Gdy undefined — przycisk „Nowy layout" jest ukryty (np. tryb embedded). */
  onCreate?: () => void;
  /** T12.H.11 — link do strony zarządzania kategoriami modułu (opcjonalny). */
  onManageCategories?: () => void;
  /** Edytuj meta layoutu (otwiera LayoutFormModal w trybie edit). */
  onEdit?: (layout: LayoutOption) => void;
  /** Duplikuj layout (tworzy kopię jako nowy layout). */
  onDuplicate?: (layout: LayoutOption) => void;
  /** Usuń layout (otwiera ConfirmDialog w callerze). Kosz jest ukryty dla layoutu domyślnego. */
  onDelete?: (layout: LayoutOption) => void;
  /** Ustaw layout jako domyślny dla swojej kategorii / dla całego modułu (gdy uniwersalny). */
  onSetDefault?: (layout: LayoutOption) => void;
  /**
   * Przełącz `previewDockEnabled` na tym konkretnym layoucie (toggle on/off, niezależny
   * per layout — w odróżnieniu od `onSetDefault` nie jest wzajemnie wykluczający).
   * Renderowane tylko gdy podany ORAZ `layout.previewDockEnabled !== undefined`
   * (moduły własne bez pojęcia doku nie mają tego pola).
   */
  onTogglePreviewDock?: (layout: LayoutOption) => void;
  /** Eksportuj layout do pliku (przeniesienie do innego tenanta). Działa też dla systemowego/centralnego. */
  onExport?: (layout: LayoutOption) => void;
  /** Importuj layout z pliku — pozycja w stopce listy, obok „Nowy layout". */
  onImport?: () => void;
}

export function LayoutSwitcher({
  layouts,
  currentLayoutId,
  categories,
  roleTemplates,
  onSelect,
  onCreate,
  onManageCategories,
  onEdit,
  onDuplicate,
  onDelete,
  onSetDefault,
  onTogglePreviewDock,
  onExport,
  onImport,
}: LayoutSwitcherProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /**
   * Dropdown renderujemy przez React portal do document.body — ucieka ze stacking
   * contextu buildera (canvas elementy używają dnd-kit z `transform: translate3d`
   * który tworzy własny stacking context — z-index alone go nie pokonuje).
   * Pozycja liczona dynamicznie z `getBoundingClientRect` trigger button + uaktualniana
   * na resize/scroll.
   */
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);

  // Recalc pozycji przy każdym otwarciu (useLayoutEffect — przed paint, brak flickeru)
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Recalc na resize/scroll gdy dropdown otwarty
  useEffect(() => {
    if (!open) return;
    const recalc = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    };
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true); // capture, by łapać scroll w innych kontenerach
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [open]);

  const formLayouts = layouts.filter((l) => l.layoutType === 'form');
  const current = formLayouts.find((l) => l.id === currentLayoutId) ?? formLayouts[0];
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const roleById = new Map((roleTemplates ?? []).map((r) => [r.id, r]));

  /**
   * T12.M.A.5 — badge ról przy layoucie.
   * Format:
   *  - puste roleIds → „Wszystkie role" (universal)
   *  - 1-2 role → "Admin, Księgowa"
   *  - 3+ ról → "Admin, Księgowa, +N"
   */
  function renderRoleBadge(roleIds: string[] | undefined) {
    if (!roleTemplates) return null; // brak listy → nie renderuj
    const ids = roleIds ?? [];
    if (ids.length === 0) {
      return (
        <Tooltip content="Layout dostępny dla wszystkich ról">
          <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary">
            Wszystkie role
          </span>
        </Tooltip>
      );
    }
    const allNames = ids.map((id) => roleById.get(id)?.name ?? '?');
    const visible = allNames.slice(0, 2);
    const extra = allNames.length - visible.length;
    const label = extra > 0 ? `${visible.join(', ')}, +${extra}` : visible.join(', ');
    // Tooltip pokazuje pełną listę jako bullet points (multi-line) — czytelniejszy niż separator
    const tooltipContent =
      allNames.length > 2 ? (
        <ul className="m-0 list-disc space-y-0.5 pl-4">
          {allNames.map((name, i) => (
            <li key={i}>{name}</li>
          ))}
        </ul>
      ) : (
        allNames.join(', ')
      );
    return (
      <Tooltip content={tooltipContent}>
        <span className="rounded bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium text-accent-700">
          {label}
        </span>
      </Tooltip>
    );
  }

  function renderCategoryBadge(categoryId: string | null) {
    if (categoryId === null) {
      return (
        <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary">
          Uniwersalny
        </span>
      );
    }
    const cat = categoryById.get(categoryId);
    if (!cat) return null;
    return (
      <span
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium"
        style={{ backgroundColor: `${cat.color}22`, color: cat.color }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
        {cat.name}
      </span>
    );
  }

  return (
    <div className="relative" data-testid="layout-switcher">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
        data-testid="layout-switcher-trigger"
      >
        <LayoutPanelTop className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
        <span className="font-medium">{current?.name ?? 'Brak layoutów'}</span>
        {current && renderCategoryBadge(current.categoryId)}
        {current && renderRoleBadge(current.roleIds)}
        {current?.isDefault && <Star className="h-3 w-3 text-warning-500" aria-label="domyślny" />}
        <ChevronDown
          className={`h-3.5 w-3.5 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        dropdownPos &&
        createPortal(
          <>
            {/* Click-outside overlay — w portalu, ucieka z stacking contextu canvasu */}
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              className="fixed z-[9999] min-w-[300px] rounded-md border border-border-primary bg-bg-primary shadow-lg"
              style={{ top: dropdownPos.top, right: dropdownPos.right }}
            >
              <div className="border-b border-border-primary p-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                  Layouty formularza
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {formLayouts.length === 0 ? (
                  <p className="px-3 py-2 text-xs italic text-text-muted">
                    Brak layoutów. Kliknij „Nowy layout" niżej.
                  </p>
                ) : (
                  formLayouts.map((layout) => {
                    const isCurrent = layout.id === current?.id;
                    // B.2+B.3 — layout centralny (producent): Edytuj/Usuń zablokowane,
                    // ale onSetDefault (gwiazdka) zostaje aktywne — to preferencja
                    // tenanta co do fallbacku, nie zmiana treści producenta.
                    const isFromMenu = Boolean(layout.menuSourceModuleId);
                    const isCentral = Boolean(layout.sourceViewSlug) || isFromMenu;
                    const centralTooltip = isCentral
                      ? t('tenant.systemModuleBuilder.layoutCentralTooltip')
                      : null;
                    const editTitle = centralTooltip ?? 'Edytuj właściwości';
                    const deleteTitle = centralTooltip ?? 'Usuń layout';
                    return (
                      <div
                        key={layout.id}
                        className={`flex items-stretch ${
                          isCurrent ? 'bg-accent-50' : 'hover:bg-bg-secondary'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            onSelect(layout.id);
                            setOpen(false);
                          }}
                          className={`flex flex-1 items-center gap-2 px-3 py-2 text-left text-sm transition ${
                            isCurrent ? 'text-accent-700' : ''
                          }`}
                          data-testid={`layout-switcher-option-${layout.id}`}
                        >
                          <span className="flex-1 truncate font-medium">{layout.name}</span>
                          {isCentral && (
                            <Tooltip content={centralTooltip}>
                              <Lock
                                className="h-3 w-3 flex-shrink-0 text-text-muted"
                                aria-hidden="true"
                                data-testid={`layout-switcher-central-badge-${layout.id}`}
                              />
                            </Tooltip>
                          )}
                          {renderCategoryBadge(layout.categoryId)}
                          {renderRoleBadge(layout.roleIds)}
                        </button>
                        {/* T12.H.11 — akcje per layout: gwiazdka-toggle / edit / delete.
                          Wszystkie zawsze widoczne (lepszy UX niż submenu w submenu). */}
                        <div className="flex items-center gap-0.5 pr-1.5">
                          {onSetDefault && !isFromMenu && (
                            <Tooltip
                              content={
                                layout.isDefault ? 'Domyślny dla kategorii' : 'Ustaw jako domyślny'
                              }
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!layout.isDefault) {
                                    onSetDefault(layout);
                                    setOpen(false);
                                  }
                                }}
                                disabled={layout.isDefault}
                                aria-label={layout.isDefault ? 'Domyślny' : 'Ustaw jako domyślny'}
                                className={`rounded p-1 transition ${
                                  layout.isDefault
                                    ? 'cursor-default text-warning-500'
                                    : 'text-text-muted hover:bg-warning-50 hover:text-warning-500'
                                }`}
                                data-testid={`layout-switcher-set-default-${layout.id}`}
                              >
                                <Star
                                  className={`h-3.5 w-3.5 ${layout.isDefault ? 'fill-warning-500' : ''}`}
                                />
                              </button>
                            </Tooltip>
                          )}
                          {onTogglePreviewDock && layout.previewDockEnabled !== undefined && (
                            <Tooltip
                              content={
                                layout.previewDockEnabled
                                  ? t('attachmentPreview.dockEnabledTooltip')
                                  : t('attachmentPreview.dockDisabledTooltip')
                              }
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTogglePreviewDock(layout);
                                }}
                                aria-label={
                                  layout.previewDockEnabled
                                    ? t('attachmentPreview.dockEnabledTooltip')
                                    : t('attachmentPreview.dockDisabledTooltip')
                                }
                                className={`rounded p-1 transition ${
                                  layout.previewDockEnabled
                                    ? 'text-accent-600 hover:bg-accent-50'
                                    : 'text-text-muted hover:bg-bg-tertiary hover:text-text-primary'
                                }`}
                                data-testid={`layout-switcher-toggle-preview-dock-${layout.id}`}
                              >
                                <PanelRight className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          )}
                          {onEdit && (
                            <Tooltip content={editTitle}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(layout);
                                  setOpen(false);
                                }}
                                disabled={isCentral}
                                aria-label={editTitle}
                                className={`rounded p-1 transition ${
                                  isCentral
                                    ? 'cursor-not-allowed text-text-muted opacity-40'
                                    : 'text-text-muted hover:bg-bg-tertiary hover:text-text-primary'
                                }`}
                                data-testid={`layout-switcher-edit-${layout.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          )}
                          {onDuplicate && (
                            <Tooltip content={t('tenant.systemModuleBuilder.layoutDuplicate')}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDuplicate(layout);
                                  setOpen(false);
                                }}
                                aria-label={t('tenant.systemModuleBuilder.layoutDuplicate')}
                                className="rounded p-1 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                data-testid={`layout-switcher-duplicate-${layout.id}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          )}
                          {onExport && (
                            <Tooltip content={t('tenant.formTransfer.exportTooltip')}>
                              <IconButton
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onExport(layout);
                                  setOpen(false);
                                }}
                                aria-label={t('tenant.formTransfer.exportTooltip')}
                                className="h-auto w-auto rounded p-1 text-text-muted [&_svg]:h-3.5 [&_svg]:w-3.5"
                                data-testid={`layout-switcher-export-${layout.id}`}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Kosz ukryty dla layoutu domyślnego — nie można usunąć widoku Default. */}
                          {onDelete && !layout.isDefault && (
                            <Tooltip content={deleteTitle}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(layout);
                                  setOpen(false);
                                }}
                                disabled={isCentral}
                                aria-label={deleteTitle}
                                className={`rounded p-1 transition ${
                                  isCentral
                                    ? 'cursor-not-allowed text-text-muted opacity-40'
                                    : 'text-text-muted hover:bg-danger-50 hover:text-danger-600'
                                }`}
                                data-testid={`layout-switcher-delete-${layout.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-border-primary p-1">
                {onCreate && (
                  <button
                    type="button"
                    onClick={() => {
                      onCreate();
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-medium text-accent-600 hover:bg-accent-50"
                    data-testid="layout-switcher-create"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Nowy layout
                  </button>
                )}
                {onImport && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Upload className="h-3.5 w-3.5" />}
                    onClick={() => {
                      onImport();
                      setOpen(false);
                    }}
                    className="w-full justify-start px-3 font-medium text-accent-600 hover:bg-accent-50"
                    data-testid="layout-switcher-import"
                  >
                    {t('tenant.formTransfer.importAction')}
                  </Button>
                )}
                {onManageCategories && (
                  <button
                    type="button"
                    onClick={() => {
                      onManageCategories();
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-text-secondary hover:bg-bg-secondary"
                    data-testid="layout-switcher-manage-categories"
                  >
                    <FolderOpen className="h-3.5 w-3.5" />
                    Zarządzaj kategoriami →
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
